import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
} from "react";
import { buildDirectRule, isDirectOverride } from "./overrides";
import type { RulesSeam } from "./seam/types";
import { useCurrentViewer } from "./seam/useCurrentViewer";
import { usePeopleAndTeams } from "./seam/usePeopleAndTeams";
import { usePermissionRules } from "./seam/usePermissionRules";
import type { Level, PeopleData, Rule, Subject, Target, Viewer } from "./types";
import { useRecordNames } from "./useRecordNames";

export interface PermissionsContextValue {
	rules: Rule[];
	loading: boolean;
	people: PeopleData;
	/** node id -> display name for records that one-off overrides target. */
	recordNames: Record<string, string>;
	/** The acting (signed-in) viewer. */
	viewer: Viewer;
	createRule: (input: Omit<Rule, "id">) => void;
	updateRule: (id: string, patch: Partial<Rule>) => void;
	deleteRule: (id: string) => void;
	/** Create/replace a one-off direct grant for a subject on a target. */
	setOverride: (target: Target, subject: Subject, level: Level) => void;
	/** Remove a subject's direct grant on a target (reset to inherited). */
	clearOverride: (target: Target, subject: Subject) => void;
	// Team/role management (A10).
	createTeam: (name: string, color?: string) => void;
	addTeamMember: (teamId: string, userId: string) => void;
	removeTeamMember: (teamId: string, userId: string) => void;
	createRole: (name: string, description?: string, color?: string) => void;
	assignRole: (userId: string, roleId: string | null) => void;
}

const noop = () => {};

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

/**
 * Connected provider — reads the wired seam (Apollo). The runtime app uses this.
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
	const rulesSeam = usePermissionRules();
	const peopleSeam = usePeopleAndTeams();
	const viewer = useCurrentViewer();
	const recordNames = useRecordNames(rulesSeam.rules);

	return (
		<PermissionsContextProvider
			rulesSeam={rulesSeam}
			recordNames={recordNames}
			people={peopleSeam.people}
			peopleLoading={peopleSeam.loading}
			createTeam={peopleSeam.createTeam}
			addTeamMember={peopleSeam.addTeamMember}
			removeTeamMember={peopleSeam.removeTeamMember}
			createRole={peopleSeam.createRole}
			assignRole={peopleSeam.assignRole}
			viewer={viewer}
		>
			{children}
		</PermissionsContextProvider>
	);
}

/**
 * Presentational provider — derives the context value from already-resolved seam
 * data. No data source of its own, so Storybook can feed it the stub seam
 * (no Apollo) while the app feeds it the GraphQL seam.
 */
export function PermissionsContextProvider({
	rulesSeam,
	people,
	peopleLoading,
	viewer,
	recordNames = {},
	children,
	createTeam = noop,
	addTeamMember = noop,
	removeTeamMember = noop,
	createRole = noop,
	assignRole = noop,
}: {
	rulesSeam: RulesSeam;
	people: PeopleData;
	peopleLoading: boolean;
	viewer: Viewer;
	recordNames?: Record<string, string>;
	children: ReactNode;
	createTeam?: PermissionsContextValue["createTeam"];
	addTeamMember?: PermissionsContextValue["addTeamMember"];
	removeTeamMember?: PermissionsContextValue["removeTeamMember"];
	createRole?: PermissionsContextValue["createRole"];
	assignRole?: PermissionsContextValue["assignRole"];
}) {
	const {
		rules,
		loading: rulesLoading,
		createRule,
		updateRule,
		deleteRule,
	} = rulesSeam;

	const setOverride = useCallback(
		(target: Target, subject: Subject, level: Level) => {
			const existing = rules.find((r) => isDirectOverride(r, target, subject));
			if (existing) updateRule(existing.id, { level });
			else createRule(buildDirectRule(target, subject, level));
		},
		[rules, createRule, updateRule],
	);

	const clearOverride = useCallback(
		(target: Target, subject: Subject) => {
			const existing = rules.find((r) => isDirectOverride(r, target, subject));
			if (existing) deleteRule(existing.id);
		},
		[rules, deleteRule],
	);

	const value = useMemo<PermissionsContextValue>(
		() => ({
			rules,
			loading: rulesLoading || peopleLoading,
			people,
			recordNames,
			viewer,
			createRule,
			updateRule,
			deleteRule,
			setOverride,
			clearOverride,
			createTeam,
			addTeamMember,
			removeTeamMember,
			createRole,
			assignRole,
		}),
		[
			rules,
			rulesLoading,
			peopleLoading,
			people,
			recordNames,
			viewer,
			createRule,
			updateRule,
			deleteRule,
			setOverride,
			clearOverride,
			createTeam,
			addTeamMember,
			removeTeamMember,
			createRole,
			assignRole,
		],
	);

	return (
		<PermissionsContext.Provider value={value}>
			{children}
		</PermissionsContext.Provider>
	);
}

export function usePermissions(): PermissionsContextValue {
	const ctx = useContext(PermissionsContext);
	if (!ctx) {
		throw new Error("usePermissions must be used within a PermissionsProvider");
	}
	return ctx;
}
